/**
 * Additive SEO content for the urgent-care surface.
 *
 * This module ONLY ADDS supplementary, keyword-relevant blocks (conditions,
 * services and local search phrases) that are rendered BELOW the existing page
 * content. It intentionally does NOT change any existing copy, headings, SEO
 * titles, meta descriptions, URLs or H1s.
 *
 * Compliance framing (consistent across the whole site):
 *   • VietClinics is an independent healthcare coordination service — not a
 *     clinic or hospital, and it does not diagnose or treat patients itself.
 *   • Every item below is phrased as help we can COORDINATE. A licensed doctor
 *     assesses the patient and decides what care is appropriate, subject to
 *     assessment and local availability.
 *   • No new "within 60 minutes" or "doctor available 24/7" promises are added.
 */

/* Conditions / symptoms travellers commonly contact us about. */
export const URGENT_CONDITIONS = [
  {
    title: 'Food poisoning & traveller’s diarrhoea',
    body: 'One of the most common reasons visitors feel unwell in Vietnam. We can arrange a doctor to assess food poisoning, traveller’s diarrhoea and stomach bugs, and advise on rehydration and treatment.',
  },
  {
    title: 'Vomiting, nausea & dehydration',
    body: 'Persistent vomiting, nausea and dehydration can quickly spoil a trip. A visiting doctor can assess you and, where appropriate, arrange rehydration or IV fluids.',
  },
  {
    title: 'Fever, influenza & respiratory infections',
    body: 'Fever, flu, sore throats, coughs and chest or respiratory infections — we can coordinate an English-speaking doctor to examine you and advise on treatment.',
  },
  {
    title: 'Stomach pain & digestive problems',
    body: 'Stomach pain, cramps, indigestion and other digestive problems are common while travelling; a doctor can assess the cause and recommend suitable care.',
  },
  {
    title: 'Allergies, rashes & skin conditions',
    body: 'Allergic reactions, hives, rashes, insect bites and other skin conditions — a doctor can assess the reaction and advise on medication where needed.',
  },
  {
    title: 'Minor injuries & wound dressing',
    body: 'Cuts, grazes, sprains and minor injuries — we can arrange wound cleaning, wound dressing and monitoring so a small injury doesn’t interrupt your trip.',
  },
  {
    title: 'Children’s medical consultations',
    body: 'Travelling with children? We can help arrange a doctor’s consultation for infants and children, with English-speaking support throughout.',
  },
  {
    title: 'Tourist & traveller medical assistance',
    body: 'General tourist medical assistance for visitors, expats and families — one English-speaking point of contact to understand your options and reach the right care.',
  },
];

/* Treatments / services we can help coordinate once a doctor assesses you. */
export const URGENT_SERVICES_EXTRA = [
  {
    title: 'IV hydration & fluid therapy',
    body: 'Where a doctor assesses it as appropriate, we can arrange IV hydration and fluid therapy for dehydration, food poisoning or fatigue — administered by a qualified nurse under doctor supervision.',
  },
  {
    title: 'Hotel doctor visits & home consultations',
    body: 'We coordinate hotel doctor visits and home or apartment consultations, so you can be seen where you are staying rather than travelling while unwell.',
  },
  {
    title: 'Medical tests & diagnostics',
    body: 'When a doctor recommends them, we can help arrange medical tests and diagnostics — such as blood tests or other investigations — at a partner clinic or laboratory.',
  },
  {
    title: 'Prescription & medication assistance',
    body: 'We can help with prescriptions and medication assistance, including written prescriptions and pharmacy guidance, following a doctor’s assessment.',
  },
  {
    title: 'Nursing support',
    body: 'For follow-up care such as injections, dressing changes or IV administration, we can coordinate qualified nursing support under a doctor’s direction.',
  },
  {
    title: 'Clinic & hospital appointments',
    body: 'If a clinic visit, specialist opinion or hospital-level care is needed, we can help arrange clinic and hospital appointments and referrals, and stay with your case.',
  },
];

/* Shared caveat shown under the conditions/services block. */
export const URGENT_SERVICES_CAVEAT =
  'VietClinics is an independent healthcare coordination service, not a clinic or hospital. '
  + 'A licensed doctor assesses every patient and decides what care is appropriate — the '
  + 'conditions and services above depend on the doctor’s assessment and local availability.';
